import React, { useState, useEffect } from "react";
import "./PopupForm.css";
import { Checkbox, FormControlLabel, FormGroup } from "@mui/material";
import { FaCheckCircle } from "react-icons/fa";
import { Snackbar, Alert } from "@mui/material";

// ✅ Dialog imports (ADDED)
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";

const PopupForm = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    contactBy: [],
  });

  const [emailError, setEmailError] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [generatedOtp, setGeneratedOtp] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Snackbar state (UNCHANGED)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // ✅ Success dialog state (ADDED)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  useEffect(() => {
    if (!isOpen && successDialogOpen) {
      setFormData({
        name: "",
        email: "",
        phone: "",
        contactBy: [],
      });
      setEmailError("");
      setOtpSent(false);
      setOtp("");
      setGeneratedOtp("");
      setEmailVerified(false);
    }
  }, [isOpen,successDialogOpen]);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const handleChange = (e) => {
    const { name, value, checked } = e.target;

    if (name === "email") {
      setFormData({ ...formData, email: value });
      setEmailVerified(false);
      setOtpSent(false);
      setOtp("");

      if (!validateEmail(value)) {
        setEmailError("Please enter a valid email");
      } else {
        setEmailError("");
      }
      return;
    }

    if (name === "contactBy") {
      let updated = [...formData.contactBy];

      if (checked) {
        // If selecting "None", remove others
        if (value === "None") {
          updated = ["None"];
        }
        // If selecting Email/Phone, remove "None"
        else {
          updated = updated.filter((v) => v !== "None");
          updated.push(value);
        }
      } else {
        // Uncheck
        updated = updated.filter((v) => v !== value);
      }

      setFormData({
        ...formData,
        contactBy: updated,
      });

      return;
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const sendOtp = async () => {
    if (!validateEmail(formData.email)) {
      setEmailError("Enter valid email before verification");
      return;
    }

    try {
      setOtpLoading(true);

      const response = await fetch(
        `https://s3napi.s3nsoftware.com/api/WordOfTheDay/VerificationEmail?EmailId=${encodeURIComponent(
          formData.email,
        )}`,
      );

      if (!response.ok) throw new Error();

      const data = await response.json();
      setGeneratedOtp(data);
      setOtpSent(true);

      showSnackbar("OTP sent to your email", "success");
    } catch {
      showSnackbar("Failed to send OTP", "error");
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOtp = () => {
    if (otp === generatedOtp) {
      setEmailVerified(true);
      showSnackbar("Email verified", "success");
    } else {
      showSnackbar("Invalid OTP", "error");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!emailVerified) {
      showSnackbar("Verify email first", "warning");
      return;
    }

    try {
      setLoading(true);

      const response = await fetch(
        "https://s3napi.s3nsoftware.com/api/WordOfTheDay/AddContactFormData",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            Name: formData.name,
            Email: formData.email,
            Phone: formData.phone,
            ContactTo: formData.contactBy.join(","),
          }),
        },
      );

      if (!response.ok) throw new Error();

      // ✅ SHOW DIALOG INSTEAD OF SNACKBAR (ONLY CHANGE)
      setSuccessDialogOpen(true);
    } catch {
      showSnackbar("Submit failed", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="popup-overlay">
      <div className="popup-box">
        <div className="popup-header">
          <h2>Contact Us</h2>
          <span className="close-btn" onClick={onClose}>
            X
          </span>
        </div>

        <p className="subtitle">
          Please fill this form to receive more information.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <input
              type="text"
              name="name"
              placeholder="Name *"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row">
            <div className="inline-row">
              <input
                type="email"
                name="email"
                placeholder="Email *"
                value={formData.email}
                onChange={handleChange}
                disabled={emailVerified}
                required
              />

              {!emailVerified && (
                <button
                  type="button"
                  className="mini-btn"
                  onClick={sendOtp}
                  disabled={otpLoading || !formData.email || emailError}
                >
                  {otpLoading ? "Sending..." : "Verify"}
                </button>
              )}

              {emailVerified && (
                <span className="verified">
                  <FaCheckCircle />
                </span>
              )}
            </div>

            {emailError && <small className="error">{emailError}</small>}
          </div>

          {otpSent && !emailVerified && (
            <div className="form-row">
              <div className="inline-row">
                <input
                  type="text"
                  placeholder="Enter OTP"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
                <button type="button" className="mini-btn" onClick={verifyOtp}>
                  Verify OTP
                </button>
              </div>
            </div>
          )}

          <div className="form-row">
            <input
              type="tel"
              name="phone"
              maxLength="10"
              placeholder="Phone number *"
              value={formData.phone}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-row contact-row">
            <p>How can we contact you?</p>
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    name="contactBy"
                    value="Email"
                    checked={formData.contactBy.includes("Email")}
                    onChange={handleChange}
                    disabled={formData.contactBy.includes("None")}
                  />
                }
                label="Email"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="contactBy"
                    value="Phone"
                    checked={formData.contactBy.includes("Phone")}
                    onChange={handleChange}
                    disabled={formData.contactBy.includes("None")}
                  />
                }
                label="Phone"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="contactBy"
                    value="None"
                    checked={formData.contactBy.includes("None")}
                    onChange={handleChange}
                    disabled={
                      formData.contactBy.includes("Email") ||
                      formData.contactBy.includes("Phone")
                    }
                  />
                }
                label="None"
              />
            </FormGroup>
          </div>

          <button
            type="submit"
            className="submit-btn"
            disabled={loading || !emailVerified}
          >
            {loading ? "Submitting..." : "Submit"}
          </button>
        </form>

        {/* Snackbar (UNCHANGED) */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={3000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert severity={snackbar.severity} variant="filled">
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* ✅ Success Dialog (ADDED ONLY) */}
        <Dialog
          open={successDialogOpen}
          onClose={() => {
            setSuccessDialogOpen(false);
            onClose();
          }}
        >
          <DialogTitle>Success</DialogTitle>
          <DialogContent>
            <p>Form submitted successfully </p>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              onClick={() => {
                setSuccessDialogOpen(false);
                onClose();
              }}
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    </div>
  );
};

export default PopupForm;
